import React, { useState } from "react";
import logo from "../assets/img/logo.svg";
import { Link, useNavigate } from "react-router-dom";
import Email from "../assets/icons/Email";
import Password from "../assets/icons/Password";
import { forgotPassword, resetPassword } from "../api/auth";
import { toast } from "sonner";
import { Spinner } from "react-bootstrap";
import { forgotPasswordValidate, resetPasswordValidate } from "../utils/Validations";
import { PATH_AUTH } from "../routes/paths";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = request reset, 2 = set new password
  const [isLoading, setIsLoading] = useState(false);
  const [requestFormData, setRequestFormData] = useState({
    email: ""
  });
  const [resetFormData, setResetFormData] = useState({
    email: "",
    code: "",
    password: "",
    confirmPassword: ""
  });
  const [requestErrors, setRequestErrors] = useState({});
  const [resetErrors, setResetErrors] = useState({});

  const handleRequestChange = (e) => {
    const { name, value } = e.target;
    setRequestFormData({
      ...requestFormData,
      [name]: value,
    });
  };

  const handleResetChange = (e) => {
    const { name, value } = e.target;
    setResetFormData({
      ...resetFormData,
      [name]: value,
    });
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate form
    const validationErrors = forgotPasswordValidate(requestFormData);
    setRequestErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setIsLoading(false);
      return;
    }

    try {
      const success = await forgotPassword(requestFormData.email);
      
      if (success) {
        toast.success("Password reset code sent to your email");
        
        // Move to next step and pre-fill email
        setResetFormData(prev => ({
          ...prev,
          email: requestFormData.email
        }));
        setStep(2);
      } else {
        throw new Error("Failed to send reset code");
      }
    } catch (error) {
      // Handle specific Cognito error messages
      let errorMessage = "Failed to send reset code. Please try again.";
      
      if (error.message && error.message.includes("UserNotFoundException")) {
        errorMessage = "No account found with this email address.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate form
    const validationErrors = resetPasswordValidate(resetFormData);
    setResetErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setIsLoading(false);
      return;
    }

    try {
      const success = await resetPassword(
        resetFormData.email,
        resetFormData.code,
        resetFormData.password
      );
      
      if (success) {
        toast.success("Password reset successful");
        
        // Redirect to login after successful password reset
        setTimeout(() => {
          navigate(PATH_AUTH.login);
        }, 2000);
      } else {
        throw new Error("Failed to reset password");
      }
    } catch (error) {
      // Handle specific Cognito error messages
      let errorMessage = "Failed to reset password. Please check your code and try again.";
      
      if (error.message && error.message.includes("ExpiredCodeException")) {
        errorMessage = "Verification code has expired. Please request a new one.";
      } else if (error.message && error.message.includes("CodeMismatchException")) {
        errorMessage = "Incorrect verification code. Please try again.";
      } else if (error.message && error.message.includes("InvalidPasswordException")) {
        errorMessage = "Password does not meet requirements. Please use at least 8 characters with uppercase, lowercase, numbers and special characters.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="equaliq eiq">
      <div className="login-page">
        <header>
          <div className="box">
            <div className="header-content">
              <Link to="/">
                <img src={logo} alt="Equal IQ Logo" />
              </Link>
            </div>
          </div>
        </header>
        <section className="login-section">
          <div className="box">
            <div className="login-content">
              <div className="login-right" style={{ margin: "0 auto" }}>
                <h4>
                  {step === 1 ? "Forgot Password" : "Reset Password"}
                  <span>{step === 1 ? "We'll send you a reset code" : "Enter the code we sent you"}</span>
                </h4>
                
                {step === 1 ? (
                  <form className="form-detail" onSubmit={handleRequestSubmit}>
                    <div className="form-input-label">
                      <h5>Email</h5>
                      <div className="form-label">
                        <Email />
                        <input
                          type="email"
                          name="email"
                          placeholder="Enter your email"
                          value={requestFormData.email}
                          onChange={handleRequestChange}
                          autoFocus
                        />
                      </div>
                      {requestErrors.email && <span className="error">{requestErrors.email}</span>}
                    </div>

                    <button type="submit" disabled={isLoading}>
                      {isLoading ? <Spinner animation="border" size="sm" /> : "Send Reset Code"}
                    </button>
                    
                    <p>
                      Remember your password? <Link to={PATH_AUTH.login}>Sign in</Link>
                    </p>
                  </form>
                ) : (
                  <form className="form-detail" onSubmit={handleResetSubmit}>
                    <div className="form-input-label">
                      <h5>Email</h5>
                      <div className="form-label">
                        <Email />
                        <input
                          type="email"
                          name="email"
                          placeholder="Enter your email"
                          value={resetFormData.email}
                          onChange={handleResetChange}
                          readOnly
                        />
                      </div>
                    </div>
                    
                    <div className="form-input-label">
                      <h5>Verification Code</h5>
                      <div className="form-label">
                        <input
                          type="text"
                          name="code"
                          placeholder="Enter verification code"
                          value={resetFormData.code}
                          onChange={handleResetChange}
                          autoFocus
                        />
                      </div>
                      {resetErrors.code && <span className="error">{resetErrors.code}</span>}
                    </div>
                    
                    <div className="form-input-label">
                      <h5>New Password</h5>
                      <div className="form-label">
                        <Password />
                        <input
                          type="password"
                          name="password"
                          placeholder="Enter new password"
                          value={resetFormData.password}
                          onChange={handleResetChange}
                        />
                      </div>
                      {resetErrors.password && <span className="error">{resetErrors.password}</span>}
                    </div>
                    
                    <div className="form-input-label">
                      <h5>Confirm Password</h5>
                      <div className="form-label">
                        <Password />
                        <input
                          type="password"
                          name="confirmPassword"
                          placeholder="Confirm new password"
                          value={resetFormData.confirmPassword}
                          onChange={handleResetChange}
                        />
                      </div>
                      {resetErrors.confirmPassword && <span className="error">{resetErrors.confirmPassword}</span>}
                    </div>

                    <button type="submit" disabled={isLoading}>
                      {isLoading ? <Spinner animation="border" size="sm" /> : "Reset Password"}
                    </button>
                    
                    <p>
                      <Link to="#" onClick={(e) => {
                        e.preventDefault();
                        setStep(1);
                      }}>
                        Back to reset request
                      </Link>
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default ForgotPassword;